import * as EmailValidator from 'email-validator';
import { ActionType, VerifyParam } from 'rc-slider-captcha';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';

import { LoginTemplate } from '../../components/Templates';
import useSignIn from '../../hooks/useSignIn';
import { useSystemStore } from '../../stores/SystemStore';
import verifyCaptcha from '../../utils/captcha';
import { useSessionStore } from '../../stores/SessionStore';
import { useNavigate } from 'react-router-dom';
import { useShareData } from '../../context';

interface State {
  email: string;
  password: string;
  isClicked: boolean;
  errorMessage: string;
  isLoading: boolean;
  captcha: number;
  showCaptcha: boolean;
  verified: boolean;
}

const { fetchCaptcha } = useSystemStore.getState();

const LoginScreen: React.FC = () => {
  const isLoggedIn = useSessionStore((state) => state.isLoggedIn);
  const navigate = useNavigate();
  const { signIn } = useSignIn();
  const { sharedData, setSharedData } = useShareData();
  const [state, setState] = useState<State>({
    email: '',
    password: '',
    isClicked: false,
    errorMessage: '',
    isLoading: false,
    captcha: 0,
    showCaptcha: false,
    verified: false,
  });
  const captchaRef = useRef<number>(state.captcha);
  const onEmailChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setState((s) => ({ ...s, email: event.target.value }));
  }, []);
  const onPasswordChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({ ...s, password: event.target.value }));
    },
    [],
  );
  const onSetErrorMessage = useCallback((message: string) => {
    setState((s) => ({ ...s, errorMessage: message }));
  }, []);
  const onClickLogin = useCallback(async () => {
    if (state.verified) {
      setState((s) => ({ ...s, isClicked: true, isLoading: true }));
      const isEmail = EmailValidator.validate(state.email);
      await signIn(
        {
          email: isEmail ? state.email : undefined,
          username: isEmail ? undefined : state.email,
          password: state.password,
          userCode: sharedData?.referralCode,
        },
        onSetErrorMessage,
        sharedData,
      );
      setSharedData(undefined);
      setState((s) => ({
        ...s,
        isLoading: false,
        verified: false,
      }));
    } else {
      onSetErrorMessage('Please verify the captcha');
    }
  }, [
    onSetErrorMessage,
    setSharedData,
    sharedData,
    signIn,
    state.email,
    state.password,
    state.verified,
  ]);

  useEffect(() => {
    captchaRef.current = state.captcha;
  }, [state.captcha]);

  const onRequest = useCallback(async () => {
    const res = await fetchCaptcha();
    if (res) {
      setState((s) => ({ ...s, captcha: res.solution }));

      const convertToDataURL = (data: number[]): Promise<string> => {
        return new Promise((resolve, reject) => {
          const arrayBuffer = new Uint8Array(data).buffer;
          const blob = new Blob([arrayBuffer], { type: 'image/png' });
          const reader = new FileReader();

          reader.onloadend = function () {
            resolve(reader.result as string);
          };

          reader.onerror = reject;

          reader.readAsDataURL(blob);
        });
      };

      const bgUrl = await convertToDataURL(res.data.background.data);
      const puzzleUrl = await convertToDataURL(res.data.slider.data);

      return { bgUrl, puzzleUrl };
    }
    return { bgUrl: '', puzzleUrl: '' };
  }, []);

  const onVerify = useCallback(async (data: VerifyParam) => {
    const res = await verifyCaptcha(captchaRef.current, {
      response: data.x,
      trail: data.trail,
    });
    if (res) {
      setState((s) => ({ ...s, verified: true, showCaptcha: false }));
    }
  }, []);

  const actionRef = useRef<ActionType>();

  const onClickCaptcha = useCallback(() => {
    setState((s) => ({ ...s, showCaptcha: true }));
  }, []);

  useEffect(() => {
    if (state.showCaptcha) {
      actionRef.current?.refresh(true);
    }
  }, [state.showCaptcha]);

  const onCloseCaptcha = useCallback(() => {
    setState((s) => ({ ...s, showCaptcha: false }));
  }, []);

  const onClickForget = useCallback(() => {
    navigate(`/forgot-password`, { state: { email: state.email } });
  }, [navigate, state.email]);

  const onClickTerms = useCallback(() => {
    window.open('https://joyajoy.com/pages/term-of-services');
  }, []);

  const onClickPrivacy = useCallback(() => {
    window.open('https://joyajoy.com/pages/privacy-policy');
  }, []);

  const onClickNavigateRegister = useCallback(() => {
    navigate('/register');
  }, [navigate]);

  useEffect(() => {
    if (isLoggedIn()) {
      navigate('/home');
    }
  }, [isLoggedIn, navigate]);

  return (
    <LoginTemplate
      email={{ value: state.email, onChange: onEmailChange }}
      password={{ value: state.password, onChange: onPasswordChange }}
      isClicked={state.isClicked}
      onClick={() => {
        onClickLogin().catch(console.error);
      }}
      errorMessage={state.errorMessage}
      isLoading={state.isLoading}
      onClickForget={onClickForget}
      showCaptcha={state.showCaptcha}
      onRequest={onRequest}
      onVerify={onVerify}
      actionRef={actionRef}
      onClickCaptcha={onClickCaptcha}
      onCloseCaptcha={onCloseCaptcha}
      verified={state.verified}
      onClickTerms={onClickTerms}
      onClickPrivacy={onClickPrivacy}
      onClickNavigateRegister={onClickNavigateRegister}
    />
  );
};

export default LoginScreen;
